import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiRequest } from "../../helpers/api";
import { AdminTableSkeleton } from "../../components/skeleton/PageSkeletons";
import { useAdminAccess } from "../AdminAccessContext";
import { SortHeader, pageWindow } from "./adminTableParts";
import CreateUserDialog from "./CreateUserDialog";
import UserQuickView from "./UserQuickView";
import { dateOnly, dateTime, displayName, formatStatus, money, paymentGroupLabels, relativeTime } from "./userFormatting";
import "./AdminUsers.css";

const pageSizes = [10, 20, 50, 100];
// Everything that shapes the list lives in the URL, so a filtered view or an open Quick View can be shared.
const filterKeys = ["search", "status", "planId", "paymentStatus", "businessStatus", "industry", "verified", "registeredFrom", "registeredTo"];
const sortOptions = [["createdAt", "Registration date"], ["name", "Name"], ["email", "Email"], ["status", "Account status"]];

// Only the plan the user is on right now. The API falls back to the newest subscription when none is current,
// so ended ones count as Free here; their history is in Quick View → Subscription.
const currentStatuses = ["active", "past_due", "halted", "authenticated", "pending"];
const PlanCell = ({ subscription }) => {
  const plan = subscription && currentStatuses.includes(subscription.status) ? subscription.plan : "Free";
  return <span className={`admin-plan-name${plan === "Free" ? " is-free" : ""}`}>{plan}</span>;
};

const AdminUsers = () => {
  const [params, setParams] = useSearchParams();
  const { can } = useAdminAccess();
  const canCreate = can("admin.users.create") || can("admin.access_control.create");
  const canViewPayments = can("admin.users.view_payments");
  const page = Math.max(1, Number.parseInt(params.get("page"), 10) || 1);
  const limit = pageSizes.includes(Number(params.get("limit"))) ? Number(params.get("limit")) : 20;
  const sort = sortOptions.some(([field]) => field === params.get("sort")) ? params.get("sort") : "createdAt";
  const direction = params.get("direction") === "ASC" ? "ASC" : "DESC";
  const quickViewUser = params.get("user");
  const quickViewTab = params.get("tab") || "overview";

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [showFilters, setShowFilters] = useState(() => filterKeys.some((key) => key !== "search" && params.get(key)));
  // Typing is kept locally and written to the URL after a pause, so each keystroke doesn't trigger a request.
  const [searchText, setSearchText] = useState(params.get("search") || "");
  const [industryText, setIndustryText] = useState(params.get("industry") || "");
  const currentRequest = useRef(0);

  // React Router's functional updates start from the last render's params, so two updates in one tick (a
  // debounced search landing as a filter changes) would drop one. Building on a ref keeps both.
  const latestParams = useRef(params);
  latestParams.current = params;
  const update = useCallback((changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(latestParams.current);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    if (resetPage) next.delete("page");
    latestParams.current = next;
    setParams(next, { replace: true });
  }, [setParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const changes = {};
      if ((params.get("search") || "") !== searchText.trim()) changes.search = searchText.trim();
      if ((params.get("industry") || "") !== industryText.trim()) changes.industry = industryText.trim();
      if (Object.keys(changes).length) update(changes);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchText, industryText, params, update]);

  const listQuery = useMemo(() => {
    const query = new URLSearchParams({ page: String(page), limit: String(limit), sort, direction });
    filterKeys.forEach((key) => {
      const value = params.get(key);
      if (value && (key !== "paymentStatus" || canViewPayments)) query.set(key, value);
    });
    return query.toString();
  }, [params, page, limit, sort, direction, canViewPayments]);

  const loadUsers = useCallback(async ({ quiet = false } = {}) => {
    const requestId = ++currentRequest.current;
    if (!quiet) setLoading(true);
    setError("");
    try {
      const nextResult = await apiRequest(`/api/admin/users?${listQuery}`);
      if (requestId === currentRequest.current) setResult(nextResult);
    } catch (requestError) {
      if (requestId === currentRequest.current) setError(requestError.message || "Unable to load customer accounts.");
    } finally {
      if (requestId === currentRequest.current) setLoading(false);
    }
  }, [listQuery]);

  useEffect(() => {
    loadUsers();
    return () => { currentRequest.current += 1; };
  }, [loadUsers]);

  const changeSort = (field) => update(sort === field ? { sort: field, direction: direction === "ASC" ? "DESC" : "ASC" } : { sort: field, direction: field === "createdAt" ? "DESC" : "ASC" });
  const clearFilters = () => {
    setSearchText("");
    setIndustryText("");
    update(Object.fromEntries(filterKeys.map((key) => [key, null])));
  };
  const openQuickView = (userId, tab) => update({ user: userId, tab: tab || null }, { resetPage: false });
  const closeQuickView = useCallback(() => update({ user: null, tab: null }, { resetPage: false }), [update]);
  const changeTab = useCallback((tab) => update({ tab: tab === "overview" ? null : tab }, { resetPage: false }), [update]);
  const refreshQuietly = useCallback(() => loadUsers({ quiet: true }), [loadUsers]);

  const activeFilters = filterKeys.filter((key) => key !== "search" && params.get(key)).length;
  const pagination = result?.pagination;
  const totalPages = Math.max(1, pagination?.totalPages || 1);
  const users = result?.users || [];
  const plans = result?.subscriptionPlans || [];
  const selectedRow = users.find((user) => String(user.id) === quickViewUser);
  const select = (key, label, options) => (
    <label>{label}
      <select aria-label={label} value={params.get(key) || ""} onChange={(event) => update({ [key]: event.target.value })}>
        {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
    </label>
  );

  return (
    <section className="workspace-dashboard admin-users-page">
      <header className="workspace-dashboard-header">
        <div>
          <p className="workspace-eyebrow">CUSTOMER DIRECTORY</p>
          <h1>Users</h1>
          <p className="workspace-intro">Accounts, business profiles, subscriptions and payments in one place. Open Quick View for the full record.</p>
        </div>
        <div className="admin-directory-links">
          {pagination && <span className="workspace-period">{pagination.total.toLocaleString()} accounts</span>}
          {canCreate && <button type="button" className="admin-create-user-button" onClick={() => setCreating(true)}><i className="ti ti-user-plus" aria-hidden="true" /> Create user</button>}
        </div>
      </header>

      <section className="workspace-panel admin-users-panel">
        <div className="admin-users-toolbar">
          <label className="admin-users-search">
            <i className="ti ti-search" aria-hidden="true" />
            <input aria-label="Search users" type="search" placeholder="Search name, email, business, industry or website" value={searchText} onChange={(event) => setSearchText(event.target.value)} />
          </label>
          <div className="admin-users-toolbar-actions">
            <button type="button" className={`admin-filter-toggle${showFilters ? " is-open" : ""}`} aria-expanded={showFilters} aria-controls="admin-user-filters" onClick={() => setShowFilters((value) => !value)}>
              <i className="ti ti-adjustments-horizontal" aria-hidden="true" /> Filters{activeFilters ? <span className="admin-filter-count">{activeFilters}</span> : null}
            </button>
            <label className="admin-users-sort">
              <span className="visually-hidden">Sort by</span>
              <select aria-label="Sort by" value={`${sort}:${direction}`} onChange={(event) => { const [field, dir] = event.target.value.split(":"); update({ sort: field, direction: dir }); }}>
                {sortOptions.flatMap(([field, label]) => [
                  <option key={`${field}:ASC`} value={`${field}:ASC`}>{label} {field === "createdAt" ? "(oldest)" : "(A–Z)"}</option>,
                  <option key={`${field}:DESC`} value={`${field}:DESC`}>{label} {field === "createdAt" ? "(newest)" : "(Z–A)"}</option>
                ])}
              </select>
            </label>
            <button type="button" className="admin-icon-button" onClick={() => loadUsers()} disabled={loading} aria-label="Refresh list"><i className="ti ti-refresh" aria-hidden="true" /></button>
          </div>
        </div>
        {showFilters && <div className="admin-user-filters" id="admin-user-filters">
          {select("status", "Account status", [["", "All statuses"], ["active", "Active"], ["inactive", "Inactive"], ["suspended", "Suspended"]])}
          {select("planId", "Subscription plan", [["", "All plans"], ...plans.map((plan) => [String(plan.id), plan.name])])}
          {canViewPayments && select("paymentStatus", "Payment status", [["", "Any payment status"], ["successful", "Last payment successful"], ["pending", "Last payment pending"], ["failed", "Last payment failed"], ["none", "No payments"]])}
          {select("businessStatus", "Business profile", [["", "Any business"], ["complete", "Profile complete"], ["incomplete", "Setup in progress"], ["none", "No profile"]])}
          <label>Industry<input aria-label="Industry" placeholder="Any industry" value={industryText} maxLength={120} onChange={(event) => setIndustryText(event.target.value)} /></label>
          {select("verified", "Email", [["", "Verified or not"], ["true", "Verified"], ["false", "Unverified"]])}
          <label>Registered from<input aria-label="Registered from" type="date" value={params.get("registeredFrom") || ""} max={params.get("registeredTo") || undefined} onChange={(event) => update({ registeredFrom: event.target.value })} /></label>
          <label>Registered to<input aria-label="Registered to" type="date" value={params.get("registeredTo") || ""} min={params.get("registeredFrom") || undefined} onChange={(event) => update({ registeredTo: event.target.value })} /></label>
          <button className="admin-filter-clear" type="button" onClick={clearFilters} disabled={!activeFilters && !searchText}>Clear all</button>
        </div>}

        {error && <div className="admin-users-error" role="alert"><span>{error}</span><button type="button" onClick={() => loadUsers()} disabled={loading}>Retry</button></div>}
        {loading && !result ? <AdminTableSkeleton label="Loading customer accounts" columns={8} /> : !error && !users.length ? (
          <div className="workspace-empty admin-users-empty"><strong>No accounts found</strong><span>{activeFilters || searchText ? "Try changing your search or filters." : "Customer accounts will appear here when users sign up."}</span>{(activeFilters > 0 || searchText) && <button type="button" className="admin-filter-clear" onClick={clearFilters}>Clear search and filters</button>}</div>
        ) : !error && <>
          <div className={`admin-users-table-wrap${loading ? " is-refreshing" : ""}`} aria-busy={loading || undefined}>
            <table className="admin-users-table">
              <thead><tr>
                <SortHeader field="name" label="User" sort={sort} direction={direction} onSort={changeSort} />
                <th>Business</th>
                <th>Plan</th>
                {canViewPayments && <th>Payment</th>}
                <SortHeader field="status" label="Status" sort={sort} direction={direction} onSort={changeSort} />
                <SortHeader field="createdAt" label="Registered" sort={sort} direction={direction} onSort={changeSort} />
                <th>Last activity</th>
                <th><span className="visually-hidden">Actions</span></th>
              </tr></thead>
              <tbody>{users.map((user) => {
                const name = displayName(user);
                const payment = user.lastPayment;
                return <tr key={user.id} className={String(user.id) === quickViewUser ? "is-selected" : undefined}>
                  <td data-label="User"><div className="admin-user-name"><span aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span><div><strong>{name}</strong><small>{user.email}</small></div></div></td>
                  <td data-label="Business">{user.business ? <div className="admin-cell-stack"><strong>{user.business.name || "Untitled business"}</strong><small>{[user.business.industry, user.business.setupStatus === "complete" ? null : "setup in progress"].filter(Boolean).join(" · ") || "—"}</small></div> : <span className="admin-muted">No profile</span>}</td>
                  <td data-label="Plan"><PlanCell subscription={user.subscription} /></td>
                  {canViewPayments && <td data-label="Payment">{payment ? <div className="admin-cell-stack"><span className={`admin-pay-pill is-${payment.group || "other"}`} title={formatStatus(payment.status)}>{paymentGroupLabels[payment.group] || formatStatus(payment.status)}</span><small>{money(payment.amount, payment.currency)} · {dateOnly(payment.createdAt)}</small></div> : <span className="admin-muted">No payments</span>}</td>}
                  <td data-label="Status"><div className="admin-cell-stack"><span className={`admin-status-badge is-${user.accountStatus || "active"}`}>{user.accountStatus || "active"}</span><small>{user.isVerified ? "Email verified" : "Email unverified"}</small></div></td>
                  <td data-label="Registered">{dateOnly(user.createdAt)}</td>
                  <td data-label="Last activity"><span title={dateTime(user.lastActivityAt)}>{relativeTime(user.lastActivityAt)}</span></td>
                  <td className="admin-row-actions-cell"><button type="button" className="admin-row-action" onClick={() => openQuickView(user.id)} aria-label={`View ${name}`}><i className="ti ti-eye" aria-hidden="true" /> View</button></td>
                </tr>;
              })}</tbody>
            </table>
          </div>
          <footer className="admin-users-pagination">
            <span>{pagination.total ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, pagination.total)} of ${pagination.total.toLocaleString()}` : "No accounts"}</span>
            <div>
              <label className="admin-page-size">Rows
                <select aria-label="Rows per page" value={limit} onChange={(event) => update({ limit: event.target.value })}>{pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}</select>
              </label>
              <nav className="admin-page-buttons" aria-label="Pagination">
                <button type="button" onClick={() => update({ page: page - 1 }, { resetPage: false })} disabled={page <= 1 || loading} aria-label="Previous page"><i className="ti ti-chevron-left" aria-hidden="true" /></button>
                {pageWindow(Math.min(page, totalPages), totalPages).map((value, index) => value === "gap"
                  ? <span key={`gap-${index}`} aria-hidden="true">…</span>
                  : <button key={value} type="button" aria-current={value === page ? "page" : undefined} onClick={() => update({ page: value }, { resetPage: false })} disabled={loading}>{value}</button>)}
                <button type="button" onClick={() => update({ page: page + 1 }, { resetPage: false })} disabled={page >= totalPages || loading} aria-label="Next page"><i className="ti ti-chevron-right" aria-hidden="true" /></button>
              </nav>
            </div>
          </footer>
        </>}
      </section>
      {quickViewUser && <UserQuickView
        key={quickViewUser}
        userId={quickViewUser}
        tab={quickViewTab}
        onTabChange={changeTab}
        onClose={closeQuickView}
        plans={plans}
        latestPayment={selectedRow?.lastPayment || null}
        onUserChanged={refreshQuietly}
      />}
      {creating && <CreateUserDialog onClose={() => setCreating(false)} onCreated={({ isStaff }) => { if (!isStaff) loadUsers(); }} />}
    </section>
  );
};

export default AdminUsers;
